import { useMsal } from "@azure/msal-react";
import { ActionIcon, Button, Group, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconClock, IconInfoCircle, IconUserPlus } from "@tabler/icons-react";
import { MRT_Table, useMantineReactTable } from "mantine-react-table";
import type React from "react";
import { useEffect, useState } from "react";
import type { RawDatasource } from "src/db";
import { fetchPendingRequests } from "src/hooks/useFetchPendingRequests";
import DatasourceRequestAccessModal from "./DatasourceRequestAccessModal";

function getColumns() {
    return [
        {
            header: "Access",
            enableSorting: false,
            size: 80,
            Cell: ({ row, table }) => {
                const [
                    accessRequestModalOpened,
                    { open: openAccessRequest, close: closeAccessRequest },
                ] = useDisclosure(false);
                const modalProps = table.options.meta as {
                    pendingRequests: Set<string>;
                    setPendingRequests: React.Dispatch<React.SetStateAction<Set<string>>>;
                };
                const isPending = modalProps?.pendingRequests?.has(row.original.id);

                if (row.original.has_access) {
                    return (
                        <Tooltip label="Access granted">
                            <div>
                                <Button
                                    aria-label="Access granted"
                                    size="xs"
                                    // compact
                                    style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#107C10",
                                        border: "none",
                                    }}
                                    disabled
                                >
                                    <IconCheck size={12} style={{ color: "white" }} />
                                </Button>
                            </div>
                        </Tooltip>
                    );
                }

                if (isPending) {
                    return (
                        <Tooltip label="Access request pending">
                            <div>
                                <Button
                                    aria-label="Access request pending"
                                    size="xs"
                                    // compact
                                    style={{
                                        padding: "4px 8px",
                                        backgroundColor: "#f59e0b",
                                        border: "none",
                                    }}
                                    disabled
                                >
                                    <IconClock size={12} style={{ color: "white" }} />
                                </Button>
                            </div>
                        </Tooltip>
                    );
                }

                return (
                    <>
                        <Tooltip label="Request access to this data source">
                            <Button
                                aria-label="Request access"
                                size="xs"
                                // compact
                                onClick={openAccessRequest}
                                style={{ padding: "4px 8px" }}
                            >
                                <IconUserPlus size={12} />
                            </Button>
                        </Tooltip>
                        <DatasourceRequestAccessModal
                            opened={accessRequestModalOpened}
                            onClose={() => {
                                closeAccessRequest();
                            }}
                            onSubmit={() => {
                                modalProps?.setPendingRequests(
                                    (prev: Set<string>) => new Set([...prev, row.original.id]),
                                );
                            }}
                            title={`Request Access to ${row.original.label || row.original.id}`}
                            description={row.original.description}
                            ingestion_status={row.original.ingestion_status}
                            requested_datasources={[row.original.id]}
                        />
                    </>
                );
            },
        },
        {
            id: "datasource_name",
            accessorFn: (row) => row.label ?? row.id,
            header: "Data Source",
            Cell: ({ row }) => (
                <Group gap={4} wrap="nowrap">
                    <Text size="sm">{row.original.label ?? row.original.id}</Text>
                    <Tooltip label="Show ingestion status and description">
                        <ActionIcon
                            aria-label="Show ingestion status and description"
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => row.toggleExpanded()}
                        >
                            <IconInfoCircle size={14} />
                        </ActionIcon>
                    </Tooltip>
                </Group>
            ),
        },
        {
            accessorFn: (row) => {
                return (row.product_lines ?? []).toSorted((a, b) => a.localeCompare(b)).join(", ");
            },
            header: "Product Lines",
            enableSorting: false,
        },
        {
            accessorFn: (row) => {
                const count = Number(row.total_count);
                return Number.isFinite(count) && count > 0 ? count : 0;
            },
            header: "Count",
            size: 60,
            sortingFn: "basic",
            Cell: ({ row }) => {
                const count = Number(row.original.total_count);
                if (!Number.isFinite(count)) return row.original.total_count;
                return count > 0 ? count.toLocaleString() : "";
            },
        },
        {
            header: "Latest Refresh Date",
            accessorKey: "latest_document_timestamp",
            sortingFn: "datetime",
            accessorFn: (row) =>
                row.latest_document_timestamp ? new Date(row.latest_document_timestamp) : undefined,
            Cell: ({ cell }) => {
                const date = cell.getValue() as Date | null;
                if (!date) return "";

                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const day = String(date.getDate()).padStart(2, "0");
                return `${year}-${month}-${day}`;
            },
        },
    ];
}

interface IngestionStatusOverviewModalProps {
    status?: RawDatasource[];
    opened: boolean;
    fullScreen: boolean;
    onClose: () => void;
}

export const IngestionStatusOverviewModal: React.FC<IngestionStatusOverviewModalProps> = ({
    status,
    opened,
    fullScreen,
    onClose,
}) => {
    const { instance } = useMsal();
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());
    const sortedValues = status?.toSorted((a, b) => a.id.localeCompare(b.id, "en"));

    useEffect(() => {
        if (opened) {
            const fetchRequests = async () => {
                try {
                    setPendingRequests(await fetchPendingRequests(instance));
                } catch (err) {
                    console.error("Failed to fetch pending requests:", err);
                }
            };
            fetchRequests();
        }
    }, [instance, opened]);

    const table = useMantineReactTable({
        layoutMode: "grid",
        columns: getColumns(),
        data: sortedValues ?? [],
        meta: { pendingRequests, setPendingRequests },
        enablePagination: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableExpanding: true,
        renderDetailPanel: ({ row }) => (
            <Stack gap={2} py={4}>
                <Text size="sm">
                    <strong>Description:</strong> {row.original.description}
                </Text>
                <Text size="sm">
                    <strong>Ingestion Status:</strong> {row.original.ingestion_status}
                </Text>
            </Stack>
        ),
        initialState: {
            density: "xs",
            sorting: [{ id: "datasource_name", desc: false }],
            columnVisibility: { "mrt-row-expand": false },
        },
        mantineTableBodyCellProps: { style: { wordWrap: "break-word" } },
    });

    return (
        <Modal
            fullScreen={fullScreen}
            closeButtonProps={{ "aria-label": "Close modal" }}
            size="100em"
            opened={opened}
            onClose={onClose}
            title={"Data Source Details"}
        >
            <MRT_Table table={table} />
        </Modal>
    );
};
